import React, { useEffect, useState } from "react";
import { Snackbar, Alert } from "@mui/material";
import { LuFileText, LuSquarePen } from "react-icons/lu";
import "./jobs.scss";
import { useDispatch, useSelector } from "react-redux";
import {
  getAction,
  getAutomation,
  getAutomationApp,
  getTrigger,
  postAutomationNodes,
  postAutomationLink,
  createAutomations,
  updateAutomation,
  triggerAutomationRun,
  getAutomationById,
} from "../../Redux/ActionCreators/automationAction";
import { fetchJobById } from "../../Redux/ActionCreators/jobsAction";
import { useParams, useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useAppPermission } from "../../hooks/useAppPermission";
import { P } from "../../constants/rbac";

const Automation = ({ jobId: propJobId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { jobId: routeJobId } = useParams();
  const jobId = propJobId || routeJobId;
  const { can } = useAppPermission();
  const canViewAutomation =
    can(P.JOBS_AUTOMATION_READ) || can(P.JOBS_AUTOMATION_EDIT) || can(P.JOBS_JOB_EDIT);

  useEffect(() => {
    if (!canViewAutomation) {
      navigate(jobId ? `/jobs/${jobId}/candidates` : "/jobs", { replace: true });
    }
  }, [canViewAutomation, jobId, navigate]);

  const [showModal, setShowModal] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);
  const [conditionField, setConditionField] = useState("");
  const [conditionValue, setConditionValue] = useState("");
  const [conditionComparator, setConditionComparator] = useState("");
  const [savingRule, setSavingRule] = useState(false);
  const [loadingAutomations, setLoadingAutomations] = useState(true);
  const [expireType, setExpireType] = useState("");
  const [expireDate, setExpireDate] = useState(null);
  const [expireDays, setExpireDays] = useState("");
  const [showExpiration, setShowExpiration] = useState(false);
  const [videoInterviewType, setVideoInterviewType] = useState("");
  const [localAutomationDetails, setLocalAutomationDetails] = useState(null);

  const latestJob = useSelector((state) => state.jobs.latestJob);

  const comparatorMap = {
    gte: "greater than or equal",
    lte: "less than or equal",
    eq: "equals",
    ne: "not equal",
    lt: "less than",
    gt: "greater than",
  };

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const [form, setForm] = useState({
    trigger: "",
    action: "",
  });

  const isVideoCallAction = form.action === "Assign video call";
  const videoCallStage = latestJob?.hiring_stages?.find(
    (s) => s?.hiring_stage?.name === "Video Call"
  );
  const isVideoSettingsMissing =
    isVideoCallAction &&
    videoInterviewType === "hybrid" &&
    videoCallStage?.data?.video_interview_settings?.enable_ai_bot !== true;

  const automationApp = useSelector(
    (state) => state.automation?.automationApp || null,
  );
  const automationDetails = useSelector(
    (state) => state.automation?.automationDetails || null,
  );
  const triggerList = useSelector(
    (state) => state.automation?.triggerDetails || [],
  );
  const actionList = useSelector(
    (state) => state.automation?.actionDetails || [],
  );

  const tenantId = localStorage.getItem("tenentId");
  const { selectedTenant } = useSelector((state) => state.users);

  const automationEnabled =
    selectedTenant?.data?.subscription_plan?.configuration?.feature_flags
      ?.automation_workflows === true || selectedTenant?.data?.subscription_plan?.configuration?.feature_flags
      ?.automation === true;

  useEffect(() => {
    setLocalAutomationDetails(automationDetails);
  }, [automationDetails]);

  useEffect(() => {
    const initialFetch = async () => {
      setLoadingAutomations(true);
      const appPayload = { page: 1, page_size: 20 };
      await dispatch(fetchJobById(jobId));
      await dispatch(getAutomationApp(appPayload));
      await dispatch(getTrigger(jobId));
      await dispatch(getAction(jobId));
      setLoadingAutomations(false);
    };
    initialFetch();
  }, [dispatch]);

  useEffect(() => {
    if (!automationApp || automationApp.length === 0) return;

    const payload = {
      job_id: jobId,
      tenant_id: tenantId,
      app_id: automationApp[0].id,
    };

    setLoadingAutomations(true);
    dispatch(getAutomation(payload)).finally(() =>
      setLoadingAutomations(false),
    );
  }, [automationApp, jobId, tenantId]);

  const tableData = React.useMemo(() => {
    if (!localAutomationDetails || !triggerList.length || !actionList.length)
      return [];

    return localAutomationDetails.map((a) => {
      const triggerNode = a.nodes?.find((n) => n.type === "trigger");
      const actionNode = a.nodes?.find((n) => n.type === "action");

      const triggerName =
        triggerList.find((t) => t.id === triggerNode?.reference_id)?.name ||
        "-";

      const actionName =
        actionList.find((a) => a.id === actionNode?.reference_id)?.name || "-";

      const link = a.links?.[0];
      const cond = link?.link_condition;

      let conditionText = "-";
      if (cond) {
        const opText = comparatorMap[cond.op] || cond.op;
        conditionText = `${opText} score ${cond.value}%`;
      }

      // ✅ EXPIRY LOGIC
      let expiry = "-";
      const config = actionNode?.config || {};

      if (config.valid_till) {
        expiry = formatExpiryDate(config.valid_till);
      } else if (config.no_of_days) {
        expiry = `${config.no_of_days} days after trigger`;
      }

      return {
        id: a.id,
        trigger: triggerName,
        action: actionName,
        active: a.is_active,
        condition: conditionText,
        expiry: expiry,
      };
    });
  }, [localAutomationDetails, triggerList, actionList]);

  const getSelectedTriggerId = () => {
    const t = triggerList.find((tr) => tr.name === form.trigger);
    return t?.id || null;
  };

  const getSelectedActionId = () => {
    const a = actionList.find((ac) => ac.name === form.action);
    return a?.id || null;
  };

  const handleSave = async () => {
    if (!form.trigger) {
      return setSnack({
        open: true,
        message: "Please select a Trigger",
        severity: "error",
      });
    }
    if (!conditionField || !conditionValue || !conditionComparator) {
      return setSnack({
        open: true,
        message: "Please complete the Condition",
        severity: "error",
      });
    }
    if (!form.action) {
      return setSnack({
        open: true,
        message: "Please select an Action",
        severity: "error",
      });
    }
    if (isVideoCallAction && !videoInterviewType) {
      return setSnack({
        open: true,
        message: "Please select an Interview Type",
        severity: "error",
      });
    }
    if (expireType === "date" && !expireDate) {
      return setSnack({
        open: true,
        message: "Please select an expire date",
        severity: "error",
      });
    }

    if (expireType === "days" && !expireDays) {
      return setSnack({
        open: true,
        message: "Please select expire days",
        severity: "error",
      });
    }

    const automationResp = await dispatch(
      createAutomations(createAutomationPayload),
    );
    if (!automationResp?.id) {
      return setSnack({
        open: true,
        message: "Failed to create automation",
        severity: "error",
      });
    }

    // Use ID returned from reducer/store
    const automationId = automationResp.id;

    const triggerId = getSelectedTriggerId();
    const actionId = getSelectedActionId();

    if (!triggerId) {
      return setSnack({
        open: true,
        message: "Invalid trigger selection",
        severity: "error",
      });
    }
    if (!actionId) {
      return setSnack({
        open: true,
        message: "Invalid action selection",
        severity: "error",
      });
    }

    try {
      setSavingRule(true);
      // 1️ CREATE TRIGGER NODE
      const triggerPayload = {
        type: "trigger",
        reference_id: triggerId,
        config: {},
        // config: { field: conditionField, comparator: conditionComparator, value: conditionValue },
        position_x: 0,
        position_y: 0,
      };

      const triggerNodeResp = await dispatch(
        postAutomationNodes(automationId, triggerPayload),
      );

      if (!triggerNodeResp?.id)
        throw new Error("Failed to create trigger node");

      // 2️ CREATE ACTION NODE
      let actionConfig = {};

      if (isVideoCallAction) {
        actionConfig = { video_interview_type: videoInterviewType };
        if (videoInterviewType === "ai_interview" && showExpiration) {
          if (expireType === "date") {
            actionConfig = {
              ...actionConfig,
              expire_at: expireDate?.toISOString(),
              valid_till: expireDate?.toISOString(),
              enable_date: true,
            };
          }
          if (expireType === "days") {
            actionConfig = {
              ...actionConfig,
              enable_days: true,
              no_of_days: Number(expireDays),
            };
          }
        }
      } else if (showExpiration) {
        if (expireType === "date") {
          actionConfig = {
            expire_at: expireDate?.toISOString(),
            valid_till: expireDate?.toISOString(),
            enable_date: true,
          };
        }

        if (expireType === "days") {
          actionConfig = {
            enable_days: true,
            no_of_days: Number(expireDays),
          };
        }
      }

      const actionPayload = {
        type: "action",
        reference_id: actionId,
        config: actionConfig,
        position_x: 0,
        position_y: 0,
      };

      const actionNodeResp = await dispatch(
        postAutomationNodes(automationId, actionPayload),
      );
      if (!actionNodeResp?.id) throw new Error("Failed to create action node");

      const trigger = triggerList.find((t) => t.id === triggerId);
      const backendField = trigger?.output_schema?.field;

      // 3️ CREATE LINK
      const linkPayload = {
        from_node_id: triggerNodeResp?.id,
        to_node_id: actionNodeResp?.id,
        link_condition: {
          field: backendField,
          op: conditionComparator,
          value: Number(conditionValue),
        },
      };

      const linkResp = await dispatch(
        postAutomationLink(automationId, linkPayload),
      );

      await dispatch(triggerAutomationRun(automationId, "IN_PROGRESS"));

      if (!linkResp?.id) throw new Error("Failed to create node link");

      // 4️ SUCCESS UI UPDATES
      setSnack({
        open: true,
        message: "Automation created successfully",
        severity: "success",
      });

      setShowModal(false);

      // Reset fields
      resetCreateRuleForm();

      // Refresh automation list
      await dispatch(
        getAutomation({
          job_id: jobId,
          tenant_id: tenantId,
          is_active: true,
          app_id: automationApp?.[0]?.id,
        }),
      );
    } catch (error) {
      setSnack({
        open: true,
        message: error.message || "Failed to create rule",
        severity: "error",
      });
    }

    setSavingRule(false);
  };

  const ruleCount = localAutomationDetails?.length || 0;
  const autoGeneratedName = `automation rule ${ruleCount + 1}`;
  const isTableReady =
    localAutomationDetails !== null &&
    triggerList.length > 0 &&
    actionList.length > 0 &&
    !loadingAutomations;

  const createAutomationPayload = {
    name: autoGeneratedName,
    description: "",
    app_id: automationApp?.[0]?.id,
    is_template: false,
    job_id: jobId,
    tenant_id: Number(tenantId),
  };

  const handleCreateRule = async () => {
    setShowModal(true);
  };

  const handleTriggerChange = async (e) => {
    setForm({ trigger: e.target.value, condition: "", action: "" });
    setConditionField("");
    setConditionValue("");
    setConditionComparator("");
  };

  const getConditionFields = () => {
    if (!form.trigger) return [];
    const trigger = triggerList.find((t) => t.name === form.trigger);
    if (!trigger) return [];
    const fieldName = trigger.output_schema?.field;
    return [fieldName];
  };

  const toggleActive = async (id) => {
    try {
      const selectedRow = localAutomationDetails.find((item) => item.id === id);
      if (!selectedRow) return;
      const updatedStatus = !selectedRow.is_active;

      const payload = {
        is_active: updatedStatus,
      };

      // 1. Update automation
      const resp = await dispatch(updateAutomation(id, payload));

      if (!resp) {
        console.error("Failed to update automation");
        return;
      }

      const runStatus = updatedStatus ? "IN_PROGRESS" : "COMPLETED";
      const runResp = await dispatch(triggerAutomationRun(id, runStatus));

      if (!runResp) {
        console.error("Failed to trigger automation run");
        return;
      }

      const updatedAutomation = await dispatch(getAutomationById(id));

      if (!updatedAutomation) {
        console.error("Failed to fetch updated automation by ID");
        return;
      }

      setLocalAutomationDetails((prev) =>
        (prev || []).map((item) => (item.id === id ? updatedAutomation : item)),
      );
    } catch (error) {
      console.error("Error updating automation:", error);
    }
  };

  const handleActionSelect = (e) => {
    const actionName = e.target.value;
    setForm({ ...form, action: actionName });
    setVideoInterviewType("");

    if (actionName === "Assign video call") {
      setShowExpiration(false);
      setExpireType("");
      setExpireDate(null);
      setExpireDays("");
      return;
    }

    const selected = actionList.find((a) => a.name === actionName);

    if (selected?.input_schema?.action_configuration?.is_required) {
      setShowExpiration(true);
      setExpireType("date");
      setExpireDays("");
    } else {
      setShowExpiration(false);
      setExpireType("");
      setExpireDate(null);
      setExpireDays("");
    }
  };

  const handleVideoInterviewTypeChange = (e) => {
    const type = e.target.value;
    setVideoInterviewType(type);
    if (type === "ai_interview") {
      setShowExpiration(true);
      setExpireType("date");
      setExpireDays("");
    } else {
      setShowExpiration(false);
      setExpireType("");
      setExpireDate(null);
      setExpireDays("");
    }
  };

  const resetCreateRuleForm = () => {
    setForm({ trigger: "", action: "" });
    setConditionField("");
    setConditionValue("");
    setConditionComparator("");
    setExpireType("");
    setExpireDate(null);
    setExpireDays("");
    setShowExpiration(false);
    setVideoInterviewType("");
  };

  function formatExpiryDate(isoDate) {
    if (!isoDate) return "-";

    const date = new Date(isoDate);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  }

  return (
    <div className="job-automation">
      <div className="cj-rules-grid">
        {automationEnabled && (
          <div className="cj-add-rule-card" onClick={handleCreateRule}>
            <span className="cj-add-rule-icon">+</span>
            <span className="cj-add-rule-text">Add Rule</span>
          </div>
        )}

        {!automationEnabled ? (
          <div
            style={{
              gridColumn: "1 / -1",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              width: "100%",
              padding: "40px 20px",
              textAlign: "center",
            }}
          >
            <h3
              style={{
                color: "#1a1a2e",
                fontWeight: 700,
                fontSize: 17,
                marginBottom: 10,
              }}
            >
              Feature Not Available
            </h3>
            <p
              style={{
                color: "#6b7280",
                fontSize: 13,
                maxWidth: 320,
                lineHeight: "1.8",
                marginBottom: 28,
              }}
            >
              This feature is not available on your current plan.
              <br />
              Please upgrade your plan to unlock this feature.
            </p>
            <button
              className="save-btn"
              style={{
                border: "none",
                backgroundColor: "#f75891",
                minWidth: "78px",
                height: "40px",
                fontWeight: "bold",
                fontSize: "14px",
                lineHeight: "18px",
                color: "#fff",
                borderRadius: "4px",
                padding:'0 12px',
              }}
              onClick={() => navigate("/plan")}
            >
              Upgrade Plan
            </button>
          </div>
        ) : !isTableReady ? (
          <div
            className="loading-overlay"
            style={{
              gridColumn: "2 / -1",
              textAlign: "center",
              height: "160px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div className="spinner-border"></div>
          </div>
        ) : (
          tableData.filter((item) => item.active).map((item) => (
            <div key={item.id} className="cj-rule-card">
              <div className="cj-rule-top">
                <div className="cj-rule-title-row">
                  <span className="cj-rule-icon">
                    <LuFileText size={24} />
                  </span>
                  <strong className="cj-rule-title">
                    {item.trigger} - {item.condition}
                  </strong>
                </div>
                <div
                  className="cj-rule-divider"
                  style={{
                    background:
                      "linear-gradient(90deg, #F75891 0%, #FF1C58 25%, #FFC857 49.52%, #00DDFF 76.44%, #0048B7 100%)",
                  }}
                />
                <div className="cj-rule-meta">
                  <div>
                    <span className="cj-rule-meta-label">Action:</span>
                    <br />
                    <span className="cj-rule-meta-value">{item.action}</span>
                  </div>
                  <div>
                    <span className="cj-rule-meta-label">Expiry:</span>
                    <br />
                    <span className="cj-rule-meta-value">{item.expiry}</span>
                  </div>
                </div>
              </div>
              <div className="cj-rule-footer">
                <label className="cj-toggle">
                  <input
                    type="checkbox"
                    checked={item.active}
                    onChange={() => toggleActive(item.id)}
                  />
                  <span className="cj-toggle-slider" />
                  <span
                    className={`cj-toggle-label ${item.active ? "cj-toggle-label--active" : ""}`}
                  >
                    {item.active ? "Active" : "Inactive"}
                  </span>
                </label>
              </div>
            </div>
          ))
        )}
      </div>

      {/* MODAL */}
      {showModal && (
        <div className="upload-modal automation-modal">
          <div className="modal-content">
            <div className="modal-head">
              <h3>Create Rule</h3>
              <div></div>
              <img
                src="/images/close-icon.svg"
                alt=""
                onClick={() => {
                  resetCreateRuleForm();
                  setShowModal(false);
                }}
              />
            </div>
            <div className="modal-input align-items-start flex-wrap">
              <div className="automation-dropdown">
                <label>Trigger *</label>
                <select
                  value={form.trigger}
                  onChange={(e) => handleTriggerChange(e)}
                >
                  <option value="">Select Trigger</option>
                  {triggerList.map((t) => (
                    <option key={t.id} value={t.name}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="automation-dropdown">
                <label>Condition *</label>
                <div className="d-flex flex-wrap gap-3">
                  <select
                    disabled={!form.trigger}
                    value={conditionField}
                    onChange={(e) => {
                      setConditionField(e.target.value);
                      setConditionValue("");
                      setConditionComparator("");
                    }}
                    style={{ width: "130px" }}
                  >
                    <option value="">Select Field</option>
                    {getConditionFields().map((field, idx) => (
                      <option key={idx} value={field}>
                        Score
                      </option>
                    ))}
                  </select>

                  <input
                    type="number"
                    min="0"
                    max="100"
                    disabled={!conditionField}
                    value={conditionValue}
                    onKeyDown={(e) => {
                      if (["-", "+", "e", "E", "."].includes(e.key)) e.preventDefault();
                    }}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === "") {
                        setConditionValue("");
                        return;
                      }
                      let value = Math.floor(Number(raw));
                      if (isNaN(value) || value < 0) value = 0;
                      if (value > 100) value = 100;
                      setConditionValue(value);
                    }}
                    placeholder="0 - 100"
                    style={{ width: "100px" }}
                  />

                  <select
                    disabled={!conditionField || conditionValue === ""}
                    value={conditionComparator}
                    onChange={async (e) => {
                      setConditionComparator(e.target.value);
                      setLoadingAction(true);
                      await dispatch(getAction(jobId));
                      setLoadingAction(false);
                      setForm({ ...form, condition: "custom_condition" });
                    }}
                    style={{ width: "175px" }}
                  >
                    <option value="">Select Comparator</option>
                    <option value="gte">≥ Greater than equal</option>
                    <option value="lte">≤ Less than equal</option>
                    <option value="eq">= Equal</option>
                    <option value="ne">≠ Not equal</option>
                    <option value="lt">&lt; Less than</option>
                    <option value="gt">&gt; Greater than</option>
                  </select>
                </div>
              </div>

              <div className="automation-dropdown">
                {!loadingAction ? (
                  <>
                    <label>Action *</label>
                    <select
                      disabled={!form.condition}
                      value={form.action}
                      // onChange={(e) => setForm({ ...form, action: e.target.value })}
                      onChange={(e) => handleActionSelect(e)}
                    >
                      <option value="">Select Action</option>
                      {actionList.map((a) => (
                        <option key={a.id} value={a.name}>
                          {a.name}
                        </option>
                      ))}
                    </select>
                  </>
                ) : (
                  <div
                    className="loading-overlay"
                    style={{ height: "34px", width: "267px" }}
                  >
                    <div className="spinner-border"></div>
                  </div>
                )}
              </div>

              {isVideoCallAction && (
                <div className="automation-dropdown">
                  <label>Interview Type *</label>
                  <select
                    value={videoInterviewType}
                    onChange={handleVideoInterviewTypeChange}
                  >
                    <option value="">Select Interview Type</option>
                    <option value="hybrid">Ai Assisted Interview</option>
                    <option value="ai_interview">AI lead Interview</option>
                  </select>
                  {isVideoSettingsMissing && (
                    <div
                      style={{
                        marginTop: "12px",
                        color: "#6b7280",
                        fontSize: "14px",
                        fontWeight: 500,
                      }}
                    >
                      You haven't added the video interview settings, please add.
                    </div>
                  )}
                </div>
              )}

              {showExpiration && (
                <div className="automation-dropdown">
                  <label>Expiration *</label>
                  <div className="d-flex gap-2">
                    <div className="expire-toggle-container">
                      <button
                        className={`toggle-btn ${expireType === "date" ? "active" : ""}`}
                        onClick={() => {
                          setExpireType("date");
                          setExpireDays("");
                        }}
                        disabled={!form.action}
                      >
                        Specific Date
                      </button>
                      <button
                        className={`toggle-btn ${expireType === "days" ? "active" : ""}`}
                        onClick={() => {
                          setExpireType("days");
                          setExpireDate(null);
                        }}
                        disabled={!form.action}
                      >
                        Days After Action
                      </button>
                    </div>

                    <div className="expire-input-area">
                      {expireType === "date" && (
                        <DatePicker
                          selected={expireDate}
                          onChange={(date) => {
                            if (date) {
                              const endOfDay = new Date(date);
                              endOfDay.setHours(23, 59, 59, 999);
                              setExpireDate(endOfDay);
                            } else {
                              setExpireDate(null);
                            }
                          }}
                          placeholderText="Select exact date..."
                          className="form-control"
                          minDate={new Date()}
                          dateFormat="MMMM d, yyyy"
                        />
                      )}

                      {expireType === "days" && (
                        <select
                          className="form-control"
                          value={expireDays}
                          onChange={(e) => setExpireDays(e.target.value)}
                        >
                          <option value="">Select duration...</option>
                          {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                            <option key={day} value={day}>
                              Expire {day} day{day > 1 ? "s" : ""} later
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="d-flex">
              {isVideoSettingsMissing ? (
                <button
                  className="upload-btn"
                  onClick={() => navigate(`/jobs/${jobId}/settings`)}
                >
                  Video Settings
                </button>
              ) : (
                <button
                  className="upload-btn"
                  disabled={savingRule}
                  style={{ backgroundColor: savingRule ? "#ccc" : "" }}
                  onClick={handleSave}
                >
                  {savingRule ? "Saving..." : "Save Rule"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
      >
        <Alert severity={snack.severity}>{snack.message}</Alert>
      </Snackbar>
    </div>
  );
};

export default Automation;
